import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "./api";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            // Uses /auth/whoami — returns 200 with {authenticated:false}
            // when anonymous. Avoids the noisy 401 in browser console
            // on every anonymous page load (the previous /auth/me path
            // raised 401 which browsers log as a red error).
            const r = await api.get("/auth/whoami");
            if (r.data?.authenticated) {
                setUser(r.data);
            } else {
                setUser(false);
            }
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // login() now resolves to one of:
    //   { kind: "ok",  user }    — fully authed, cookie + CSRF set
    //   { kind: "mfa", mfa_token } — partial; caller must finish via verifyMfa
    const login = async (kind, email, password) => {
        const url = kind === "admin" ? "/auth/admin/login" : "/auth/partner/login";
        const r = await api.post(url, { email, password });
        if (r.data?.mfa_required) {
            return { kind: "mfa", mfa_token: r.data.mfa_token };
        }
        if (r.data?.mfa_setup_required) {
            return { kind: "mfa_setup", mfa_token: r.data.mfa_token };
        }
        setUser(r.data);
        return { kind: "ok", user: r.data };
    };

    const verifyMfa = async (mfa_token, code) => {
        const r = await api.post("/auth/mfa/login-verify", { mfa_token, code });
        setUser(r.data);
        return r.data;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* swallow */ }
        setUser(false);
    };

    return <Ctx.Provider value={{ user, loading, login, verifyMfa, logout, refresh, setUser }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
